import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Radar, Loader2, CheckCircle2, X, ExternalLink, Ban } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("user_token") || localStorage.getItem("admin_token");
const fmtInt = (n) => Number(n || 0).toLocaleString("id-ID");

// Floating, app-wide indicator for the background "Pindai Tagihan Menunggak" job.
// Keeps polling the backend scan status even when the user navigates elsewhere, so the
// scan can run in the background and the result auto-saves to history when finished.
export default function TunggakanScanIndicator() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);   // live running snapshot
  const [finished, setFinished] = useState(null); // completed snapshot (done card)
  const pollRef = useRef(null);
  const activeRef = useRef(false);

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    activeRef.current = false;
  }, []);

  const poll = useCallback(async () => {
    const t = getToken();
    if (!t) return;
    try {
      const res = await axios.get(`${API}/tunggakan/scan/status`, { headers: { Authorization: `Bearer ${t}` } });
      const st = res.data || {};
      if (st.running) {
        setStatus(st);
        return;
      }
      // Not running. If we were tracking an active scan and it has finished -> show done card.
      if (st.finished_at && localStorage.getItem("tunggakan_scan_active") === "1") {
        localStorage.setItem("tunggakan_scan_active", "0");
        try { sessionStorage.setItem("tunggakan_scan_finished", JSON.stringify(st)); } catch { /* ignore */ }
        setStatus(null);
        setFinished(st);
        stopPoll();
        window.dispatchEvent(new Event("tunggakan-scan-done"));
        if (st.saved_list_id) {
          toast.success(`Pindai selesai — ${fmtInt(st.matched)} menunggak ditemukan. Daftar tersimpan otomatis.`);
        } else {
          toast.success(`Pindai selesai — ${fmtInt(st.matched)} menunggak, ${fmtInt(st.failed)} gagal cek.`);
        }
      } else {
        setStatus(null);
        stopPoll();
      }
    } catch { /* keep polling silently */ }
  }, [stopPoll]);

  const startPolling = useCallback(() => {
    if (activeRef.current || !getToken()) return;
    activeRef.current = true;
    poll();
    pollRef.current = setInterval(poll, 2000);
  }, [poll]);

  useEffect(() => {
    if (localStorage.getItem("tunggakan_scan_active") === "1" && getToken()) {
      startPolling();
    } else {
      // Re-hydrate the completed "done" card if the user navigated away and came back.
      try {
        const saved = sessionStorage.getItem("tunggakan_scan_finished");
        if (saved) setFinished(JSON.parse(saved));
      } catch { /* ignore */ }
    }
    const onStarted = () => {
      try { sessionStorage.removeItem("tunggakan_scan_finished"); } catch { /* ignore */ }
      setFinished(null);
      startPolling();
    };
    window.addEventListener("tunggakan-scan-started", onStarted);
    return () => { window.removeEventListener("tunggakan-scan-started", onStarted); stopPoll(); };
  }, [startPolling, stopPoll]);

  const dismissFinished = () => {
    try { sessionStorage.removeItem("tunggakan_scan_finished"); } catch { /* ignore */ }
    setFinished(null);
  };

  const cancelScan = async () => {
    const t = getToken();
    try {
      await axios.post(`${API}/tunggakan/scan/cancel`, {}, { headers: { Authorization: `Bearer ${t}` } });
      toast("Membatalkan pemindaian…");
    } catch { /* ignore */ }
  };

  if (!status && !finished) return null;

  const pct = status && status.total > 0 ? Math.round((status.processed / status.total) * 100) : 0;
  const phaseLabel =
    status?.phase === "retry" ? "Cek ulang yang gagal…" :
    status?.phase === "saving" ? "Menyimpan daftar…" : "Memindai tunggakan";

  return (
    <div className="fixed bottom-4 right-4 z-[130] w-[290px] max-w-[calc(100vw-2rem)]" data-testid="tunggakan-scan-indicator">
      {status ? (
        <div className="bg-white rounded-2xl shadow-2xl border border-rose-200 overflow-hidden">
          <div className="bg-gradient-to-r from-rose-600 to-pink-600 text-white px-3 py-2 flex items-center gap-2">
            <Radar className="w-4 h-4 animate-spin" />
            <span className="font-bold text-sm flex-1">{phaseLabel}</span>
            <Loader2 className="w-4 h-4 animate-spin opacity-80" />
          </div>
          <div className="p-3 space-y-2">
            <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
              <div className="bg-rose-600 h-2.5 transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs text-slate-600 font-medium" data-testid="scan-indicator-progress">
              {fmtInt(status.processed)} / {fmtInt(status.total)} diperiksa ({pct}%)
            </div>
            <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
              <span>Menunggak: <b className="text-rose-600">{fmtInt(status.matched)}</b></span>
              <span>· Gagal: <b className="text-amber-600">{fmtInt(status.failed)}</b></span>
              {status.not_found ? <span>· Tak ada: {fmtInt(status.not_found)}</span> : null}
            </div>
            {status.failed > 0 && status.phase !== "retry" && (
              <div className="text-[10px] text-amber-600 leading-tight" data-testid="scan-retry-hint">
                Gagal jaringan akan dicek ulang otomatis di akhir pemindaian.
              </div>
            )}
            {status.current_id && (
              <div className="text-[11px] font-mono text-slate-400 truncate">Cek: {status.current_id}</div>
            )}
            <button
              onClick={cancelScan}
              className="w-full mt-1 text-xs font-semibold text-rose-600 border border-rose-200 rounded-lg py-1.5 hover:bg-rose-50 flex items-center justify-center gap-1"
              data-testid="scan-indicator-cancel-btn"
            >
              <Ban className="w-3.5 h-3.5" /> Batalkan
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-2xl border border-emerald-200 overflow-hidden" data-testid="scan-indicator-done-card">
          <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-3 py-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <span className="font-bold text-sm flex-1">Pindai Selesai</span>
            <button onClick={dismissFinished} className="p-0.5 hover:bg-white/20 rounded" data-testid="scan-indicator-dismiss-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-3 space-y-2">
            <div className="text-xs text-slate-600">
              Ditemukan <b className="text-rose-600">{fmtInt(finished.matched)}</b> pelanggan menunggak
              {finished.failed ? <> · <b className="text-amber-600">{fmtInt(finished.failed)}</b> gagal cek</> : null}
              {finished.not_found ? <> · {fmtInt(finished.not_found)} tak ditemukan</> : null}
            </div>
            {finished.failed > 0 && (
              <div className="text-[10px] text-amber-600 leading-tight" data-testid="scan-done-failed-hint">
                {fmtInt(finished.failed)} pelanggan gagal dicek karena jaringan PDAM. Coba jalankan pindai lagi untuk mengecek sisanya.
              </div>
            )}
            {finished.saved_list_id ? (
              <button
                onClick={() => { navigate(`/tunggakan/${finished.saved_list_id}`); dismissFinished(); }}
                className="w-full text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg py-2 flex items-center justify-center gap-1.5"
                data-testid="scan-indicator-open-list-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Buka Daftar Tersimpan
              </button>
            ) : (
              <div className="text-[11px] text-slate-500">Tidak ada pelanggan menunggak yang cocok dengan kriteria.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
